'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { prefersReducedMotion } from '@/lib/motion';

/**
 * Dove ci si trova dopo aver cliccato un link. Due casi, tutti e due sbagliati
 * di loro e sistemati qui, in un punto solo invece che nei cinque menu del
 * sito (logo, nav desktop, drawer, footer, CTA sparse nelle pagine).
 *
 *  - il link porta alla pagina in cui sei già: il router considera la
 *    navigazione conclusa perché la rotta non cambia, e resti dove eri, con
 *    l'impressione che il click sia andato perso. Qui si torna in cima.
 *  - il link porta a un'altra pagina: il router non sempre riporta in cima, e
 *    passando da una pagina lunga a un'altra si atterra a metà — o in fondo.
 *    Qui si riparte dall'inizio, come farebbe un caricamento vero.
 *
 * Indietro e avanti del browser restano fuori: lì tornare al punto in cui si
 * era è esattamente quello che ci si aspetta.
 */
const NavigationScroll = () => {
  const pathname = usePathname();
  const cameFromHistory = useRef(false);
  const firstRender = useRef(true);

  /* ---- link verso la pagina in cui si è già ---- */
  useEffect(() => {
    /** Normalizza `/fonti` e `/fonti/` allo stesso indirizzo. */
    const normalise = (path: string) => (path.length > 1 ? path.replace(/\/+$/, '') : path);

    const onClick = (event: MouseEvent) => {
      // Niente controllo su `defaultPrevented`: next/link lo chiama sempre, per
      // navigare da sé — filtrando su quello il gestore non partirebbe mai.
      // Restiamo in fase di risalita, così chi ha un suo onClick (il drawer che
      // si chiude, per dire) lo esegue comunque.
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const anchor = (event.target as HTMLElement | null)?.closest?.('a[href]');
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      if (anchor.origin !== window.location.origin) return;
      // Un'ancora interna deve poter portare al suo punto: non è «la stessa pagina».
      if (anchor.hash) return;
      if (normalise(anchor.pathname) !== normalise(pathname)) return;

      event.preventDefault();
      const reduced = prefersReducedMotion();
      window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
    };

    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [pathname]);

  /* ---- indietro e avanti: la posizione la ripristina il browser ---- */
  useEffect(() => {
    const onPopState = () => {
      cameFromHistory.current = true;
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  /* ---- cambio pagina: si riparte dall'alto ---- */
  useEffect(() => {
    // Al primo montaggio no: la pagina è appena stata caricata e il browser
    // potrebbe aver già ripristinato una posizione, o l'indirizzo avere un'ancora.
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (cameFromHistory.current) {
      cameFromHistory.current = false;
      return;
    }
    if (window.location.hash) return;
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [pathname]);

  return null;
};

export default NavigationScroll;
